import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Siren, Timer, Check, ChevronsUpDown, PencilLine, CalendarDays } from "lucide-react";
import {
  OPTION_FIELDS, EMPTY_CODE_BLUE, formatRM, hitungRespon, isCodeBlueSelected, todayISO, EwsDot, rapikanNamaPoli,
  fmtTanggalPanjang,
} from "@/lib/siport";

const LAINNYA = "__lainnya__";

function FormRow({ label, required, error, hint, children, id }) {
  return (
    <div className="min-w-0 space-y-1.5" id={id ? `field-${id}` : undefined}>
      <label className="block text-sm font-semibold text-slate-800">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs font-medium text-rose-600" data-testid={`error-${id}`}>{error}</p>}
    </div>
  );
}

function OptionSelect({ value, onChange, items, placeholder, testId, big, invalid, showDot }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className={`bg-white text-left ${big ? "h-12 text-base" : ""} ${invalid ? "border-rose-400" : ""}`}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        {items.map((o) => (
          <SelectItem key={o.id} value={o.id} className={big ? "py-3 text-base" : ""} data-testid={`${testId}-opt-${o.id}`}>
            <span className="flex items-center gap-2">
              {showDot && <EwsDot warna={o.warna} />}
              <span className="whitespace-normal">{o.label}</span>
              {o.aktif === false && <span className="text-xs text-slate-400">(nonaktif)</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Combobox poliklinik: ketik untuk mencari (±100 poli), "Lainnya (isi manual)" selalu di paling bawah.
function PoliCombobox({ value, manual, onChange, items, big, invalid }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((p) => p.id === value);
  const text = manual ? "Lainnya (isi manual)" : selected?.nama;
  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} data-testid="siport-poli"
          className={`flex w-full items-center justify-between rounded-md border bg-white px-3 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-ring ${
            big ? "h-12 text-base" : "h-9 text-sm"} ${invalid ? "border-rose-400" : "border-input"}`}>
          <span className={text ? "text-slate-900 truncate" : "text-muted-foreground"}>{text || "Pilih / ketik nama poliklinik"}</span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Cari poliklinik…" className={big ? "text-base" : ""} data-testid="siport-poli-search" />
          <CommandList className="max-h-[min(300px,calc(var(--radix-popover-content-available-height)-120px))] min-h-[96px]">
            <CommandEmpty className="py-3 text-center text-sm text-slate-500">Tidak ditemukan — pilih “Lainnya” di bawah.</CommandEmpty>
            <CommandGroup>
              {items.map((p) => (
                <CommandItem key={p.id} value={p.nama} onSelect={() => pick(p.id)}
                  className={big ? "py-3 text-base" : ""} data-testid={`siport-poli-opt-${p.id}`}>
                  <Check className={`w-4 h-4 mr-2 ${!manual && value === p.id ? "opacity-100" : "opacity-0"}`} />
                  {p.nama}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {/* Di luar area gulir -> selalu terlihat di bawah daftar tanpa perlu menggulir */}
          <div className="border-t border-slate-200 p-1">
            <button type="button" onClick={() => pick(LAINNYA)} data-testid="siport-poli-lainnya"
              className={`flex w-full items-center rounded-sm px-2 font-medium text-brand-teal hover:bg-accent focus:bg-accent focus:outline-none ${
                big ? "py-3 text-base" : "py-1.5 text-sm"}`}>
              <PencilLine className="w-4 h-4 mr-2" /> Lainnya (isi manual)
              {manual && <Check className="w-4 h-4 ml-auto" />}
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Field form Laporan SIPORT — dipakai form publik (big=true) dan dialog edit admin.
 * `options` = { kategori: [{id,label,warna,trigger_code_blue,aktif?}] }, `poliklinik` = [{id,nama}].
 */
export default function SiportFormFields({
  form, setForm, options, poliklinik, poliManual = [], errors = {}, big = false, hariIni, tanggalOtomatis = false,
}) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = big ? "h-12 text-base bg-white" : "bg-white";
  const codeBlue = isCodeBlueSelected(form, options);
  const respon = hitungRespon(form.jam_aktivasi, form.jam_tmrc_datang);
  const ews = (options.ews_score || []).find((o) => o.id === form.ews_score);

  // Pilihan selain Code Blue -> kosongkan field 14–18 supaya tidak ikut tersimpan
  const onTindakLanjut = (v) => {
    const trig = (options.tindak_lanjut || []).find((o) => o.id === v)?.trigger_code_blue;
    setForm((f) => ({ ...f, tindak_lanjut: v, ...(trig ? {} : EMPTY_CODE_BLUE) }));
  };

  const onPoli = (v) =>
    setForm((f) => (v === LAINNYA
      ? { ...f, poliklinik_manual: true, poliklinik_id: "" }
      : { ...f, poliklinik_manual: false, poliklinik_id: v, poliklinik_nama_manual: "" }));

  const toggleAlasan = (id, checked) =>
    setForm((f) => ({
      ...f,
      cb_alasan: checked ? [...f.cb_alasan, id] : f.cb_alasan.filter((x) => x !== id),
    }));

  return (
    <div className="space-y-5">
      {tanggalOtomatis ? (
        // Form publik: tanggal diisi server (WIB) saat laporan diterima, tidak bisa diubah pengisi.
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
          data-testid="siport-tanggal-info">
          <CalendarDays className="w-4 h-4 shrink-0 text-slate-500" />
          <span>Tanggal Input Form: <span className="font-semibold text-slate-900">{fmtTanggalPanjang(hariIni || todayISO())}</span></span>
        </div>
      ) : (
        <FormRow label="Tanggal Input Form" required error={errors.tanggal_kejadian} id="tanggal_kejadian"
          hint="Terisi otomatis saat laporan dikirim; ubah hanya untuk koreksi.">
          <Input type="date" value={form.tanggal_kejadian} max={hariIni || todayISO()} onChange={setE("tanggal_kejadian")}
            className={`${inputCls} input-datetime`} data-testid="siport-tanggal" />
        </FormRow>
      )}

      <FormRow label="Poliklinik" required error={errors.poliklinik_id} id="poliklinik_id"
        hint={form._poliklinik_nama ? `Data lama: ${form._poliklinik_nama}. Pilih poliklinik bila diketahui.` : null}>
        <PoliCombobox value={form.poliklinik_id} manual={form.poliklinik_manual} onChange={onPoli} big={big}
          invalid={errors.poliklinik_id} items={poliklinik} />
        {!form.poliklinik_manual && (
          <button type="button" onClick={() => onPoli(LAINNYA)} data-testid="siport-poli-manual-link"
            className="text-xs font-medium text-brand-teal underline underline-offset-2 hover:text-primary">
            Poli tidak ada di daftar? Isi manual
          </button>
        )}
      </FormRow>

      {form.poliklinik_manual && (
        <FormRow label="Nama Poliklinik" required error={errors.poliklinik_nama} id="poliklinik_nama"
          hint="Poli tidak ada di daftar. Pilih saran yang muncul bila sama, supaya penulisan seragam.">
          <Input value={form.poliklinik_nama_manual} onChange={setE("poliklinik_nama_manual")}
            onBlur={(e) => set("poliklinik_nama_manual")(rapikanNamaPoli(e.target.value))}
            list="siport-poli-manual-list" autoComplete="off" maxLength={100} className={inputCls}
            placeholder="mis. Poli Luka Bakar" data-testid="siport-poli-manual" autoFocus={big} />
          <datalist id="siport-poli-manual-list">
            {poliManual.map((n) => <option key={n} value={n} />)}
          </datalist>
        </FormRow>
      )}

      <FormRow label="Nama Pengisi Form" required error={errors.nama_dokter_pengisi} id="nama_dokter_pengisi">
        <Input value={form.nama_dokter_pengisi} onChange={setE("nama_dokter_pengisi")} className={inputCls}
          placeholder="mis. dr. Andi, Sp.PD / Ns. Rina" maxLength={150} data-testid="siport-dokter" />
      </FormRow>

      <FormRow label="Nama Pasien" required error={errors.nama_pasien} id="nama_pasien">
        <Input value={form.nama_pasien} onChange={setE("nama_pasien")} className={inputCls} maxLength={150}
          autoComplete="off" data-testid="siport-pasien" />
      </FormRow>

      <div className="grid grid-cols-2 gap-3">
        <FormRow label="Usia (tahun)" required error={errors.usia} id="usia">
          <Input type="number" inputMode="numeric" min={0} max={120} step={1} value={form.usia}
            onChange={(e) => set("usia")(e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
            className={inputCls} placeholder="0–120" data-testid="siport-usia" />
        </FormRow>
        <FormRow label="No. Rekam Medis" required error={errors.no_rm} id="no_rm">
          <Input inputMode="numeric" value={form.no_rm} onChange={(e) => set("no_rm")(formatRM(e.target.value))}
            className={`${inputCls} tabular-nums`} placeholder="xxx-xx-xx" autoComplete="off" data-testid="siport-rm" />
        </FormRow>
      </div>

      <FormRow label="Diagnosa Medis Pasien" required error={errors.diagnosa} id="diagnosa">
        <Textarea value={form.diagnosa} onChange={setE("diagnosa")} rows={3} maxLength={2000}
          className={`bg-white ${big ? "text-base" : ""}`} data-testid="siport-diagnosa" />
      </FormRow>

      {OPTION_FIELDS.map(([k, label]) => (
        <FormRow key={k} label={label} required error={errors[k]} id={k}>
          <OptionSelect value={form[k]} onChange={k === "tindak_lanjut" ? onTindakLanjut : set(k)} big={big}
            invalid={errors[k]} items={(options[k] || []).filter((o) => o.aktif !== false || o.id === form[k])} placeholder="Pilih" testId={`siport-${k}`} showDot={k === "ews_score"} />
          {k === "ews_score" && ews && (
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold text-white mt-1"
              style={{ backgroundColor: ews.warna || "#64748B" }} data-testid="siport-ews-badge">
              {ews.label}
            </span>
          )}
        </FormRow>
      ))}

      {codeBlue && (
        <div className="rounded-xl border-2 border-rose-200 bg-rose-50/60 p-4 space-y-5" data-testid="siport-code-blue-section">
          <div className="flex items-center gap-2 text-rose-700 font-bold">
            <Siren className="w-5 h-5" /> Data Aktivasi Code Blue
          </div>

          <FormRow label="Alasan Aktivasi Code Blue" required error={errors.cb_alasan} id="cb_alasan"
            hint="Boleh pilih lebih dari satu">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(options.alasan_code_blue || []).filter((o) => o.aktif !== false || form.cb_alasan.includes(o.id)).map((o) => (
                <label key={o.id}
                  className={`flex items-center gap-3 rounded-lg border bg-white px-3 ${big ? "py-3 text-base" : "py-2 text-sm"} cursor-pointer ${
                    form.cb_alasan.includes(o.id) ? "border-primary ring-1 ring-primary" : "border-slate-200"}`}>
                  <Checkbox checked={form.cb_alasan.includes(o.id)} onCheckedChange={(c) => toggleAlasan(o.id, !!c)}
                    className="h-5 w-5" data-testid={`siport-cb-alasan-${o.id}`} />
                  {o.label}
                </label>
              ))}
              <label className={`flex items-center gap-3 rounded-lg border bg-white px-3 ${big ? "py-3 text-base" : "py-2 text-sm"} cursor-pointer ${
                form.cb_lainnya_on ? "border-primary ring-1 ring-primary" : "border-slate-200"}`}>
                <Checkbox checked={form.cb_lainnya_on} className="h-5 w-5" data-testid="siport-cb-lainnya-toggle"
                  onCheckedChange={(c) => setForm((f) => ({ ...f, cb_lainnya_on: !!c, cb_lainnya: c ? f.cb_lainnya : "" }))} />
                Lainnya
              </label>
            </div>
            {form.cb_lainnya_on && (
              <Input value={form.cb_lainnya} onChange={setE("cb_lainnya")} placeholder="Tuliskan alasan lainnya"
                className={`${inputCls} mt-2`} maxLength={200} data-testid="siport-cb-lainnya" />
            )}
            {errors.cb_lainnya && <p className="text-xs font-medium text-rose-600">{errors.cb_lainnya}</p>}
          </FormRow>

          {/* minmax(0,1fr): kolom boleh menyempit di bawah lebar intrinsik input time (iOS) */}
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-end gap-3">
            <FormRow label="Jam Aktivasi" required error={errors.jam_aktivasi} id="jam_aktivasi">
              <Input type="time" value={form.jam_aktivasi} onChange={setE("jam_aktivasi")} className={`${inputCls} input-datetime`}
                data-testid="siport-jam-aktivasi" />
            </FormRow>
            <FormRow label="Jam TMRC Datang" required error={errors.jam_tmrc_datang || respon.error} id="jam_tmrc_datang">
              <Input type="time" value={form.jam_tmrc_datang} onChange={setE("jam_tmrc_datang")} className={`${inputCls} input-datetime`}
                data-testid="siport-jam-tmrc" />
            </FormRow>
          </div>

          <FormRow label="Respon Time TMRC" hint="Dihitung otomatis dari jam aktivasi & jam TMRC datang">
            <div className={`flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 ${big ? "h-12 text-base" : "h-9 text-sm"} font-semibold text-slate-800`}
              data-testid="siport-respon-time">
              <Timer className="w-4 h-4 text-slate-500" />
              <span className="tabular-nums">{respon.menit != null ? respon.menit : "–"} menit</span>
            </div>
          </FormRow>

          <FormRow label="Akhir Code Blue" required error={errors.akhir_code_blue} id="akhir_code_blue"
            hint="Kondisi / kronologi akhir, mis. “Transfer IGD, terpasang IVFD NaCl 0,9%”">
            <Textarea value={form.akhir_code_blue} onChange={setE("akhir_code_blue")} rows={3} maxLength={2000}
              className={`bg-white ${big ? "text-base" : ""}`} data-testid="siport-akhir-cb" />
          </FormRow>
        </div>
      )}
    </div>
  );
}
